/**
 * @file   src\pages\Home.tsx
 * @brief  Home page.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useNavigate, useLocation, Link, useParams , useEffect} from "../utils/thirdpartyComponents";
import { Button } from "react-bootstrap";
import CWFooter from "src/components/CWFooter";
import Mappinicon from "../assets/images/location-icon.svg";
import Gobackicon from "../assets/images/left-arrow-small.svg";
import { pageURLs } from "src/utils/pageUrls";
import { CommonMessages } from "src/messages/validationMessages";
import RadioButton from "src/components/RadioButton";
import { LocationProperty } from "src/interfaces/generalInterface";
import axios from '../config/axiosConfig';
import { LocationApi} from "../utils/apiUrls";
import { MessageToaster } from "src/utils/toastUtil";
import { locationUpdateRequest} from '../interfaces/generalInterface';
import { ILocation, IMappedLocation } from "src/interfaces/authInterface";
import { getBayTypes, getFromLocalStorage, syncFreeSingleWashByLocation, navigateToSharePaymentIfPending, resolveProductSignupLocations, mapFetchedLocationToDisplay, setActiveLocale } from "src/utils/helper";
import { IBayType } from "src/interfaces/bayinterface";
import { ServiceType } from "src/utils/enums";

const SwitchLocation = () => {

    const navigate = useNavigate()
    // Instantiate MessageToaster
    const toaster = new MessageToaster();
    // Route information variable
    const params = useParams();
    // To get path from which redirection was done
    const location = useLocation();

    const origin = location.state?.origin || 'Unknown';
    const frompath = location.state?.frompath || 'Unknown';
    const carwashcompanydata = getFromLocalStorage('CARWASH_COMPANY_DATA')


    // Initialise state for list of locations
    const [locations, setLocations] = useState<IMappedLocation[]>([])

    

    // State to handle selected location
    const [selectedLocation, setSelectedLocation] = useState<LocationProperty | null>(
        (origin === "VerificationModal" || origin === "ProductSignUpVerificationModal" || frompath === 'product-signup') ? null :
        {
            "locationName": "",
            "locationId":localStorage.getItem('selectedLocation') ? parseInt(localStorage.getItem('selectedLocation') as string) :0,
            "address": ""
        },)

    // State to handle selected location before clicking OK
    const [selected, setSelected] = useState<number>(0)

    // Initialize state for errors
    const [error, setError] = useState<string | null>(null)

    // Need call location fetch API
    useEffect(() => {
        fetchLocation();
        if (frompath !== 'product-signup' && origin !== 'ProductSignUpVerificationModal') {
            fetchSelectedUserLocation()  // Selected location details
        }
    }, []);

    const fetchLocation = async() => {
        const locationData = await axios.get(LocationApi.LOCATION_FETCH);
        const allFetched: ILocation[] = locationData?.data?.data || [];

        if (frompath === 'product-signup') {
            const { locations, isProductShareFlow } = resolveProductSignupLocations(allFetched);

            if (isProductShareFlow && locations.length === 0) {
                toaster.toastError("No locations available for this subscription.");
                navigate("/"+params.carwashname+"/"+pageURLs.home);
                return;
            }

            if (isProductShareFlow) {
                if (locations.length === 1) {
                    const locationId = locations[0]?.locationId;
                    localStorage.setItem('selectedLocation', locationId?.toString());
                    const locationUpdateData: locationUpdateRequest = { locationId };
                    const locationUpdate = await axios.post(LocationApi.LOCATION_UPDATE, locationUpdateData);
                    if (locationUpdate?.data?.error === 0) {
                        syncFreeSingleWashByLocation(Number(locationId || 0));
                        if (navigateToSharePaymentIfPending(navigate, params.carwashname || '')) {
                            return;
                        }
                        navigate("/"+params.carwashname+"/"+pageURLs.home);
                    } else {
                        toaster.toastError(locationUpdate.data.error);
                    }
                } else {
                    setLocations(locations);
                }
                return;
            }
        }

        if(allFetched?.length === 1) {
            const locationId = allFetched[0]?.locationId
            localStorage.setItem('selectedLocation', locationId?.toString())
            const locationUpdateData: locationUpdateRequest = {
              locationId: locationId
            };
            const locationUpdate = await axios.post(LocationApi.LOCATION_UPDATE, locationUpdateData);
            if(locationUpdate?.data?.error === 0) {
                syncFreeSingleWashByLocation(Number(locationId || 0));
                navigate("/"+params.carwashname+"/"+pageURLs.home)
            } else {
                toaster.toastError(locationUpdate.data.error);
            }
        } else {
            const locations: IMappedLocation[] = allFetched.map(mapFetchedLocationToDisplay);
            setLocations(locations);
        }     
    }

    // Function to handle radio button selection
    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setError(null)
        setSelected(parseInt(event.target.value))
    };


    // Function to handle OK button click
    const handleOkButtonClick = async() => {
        if (selected) {
            const location = locations.find(loc => loc.locationId == selected) ;
           
          
            const locationUpdateData: locationUpdateRequest = {
                locationId: selected
            };
            
            const locationUpdate = await axios.post(LocationApi.LOCATION_UPDATE, locationUpdateData);
            if(locationUpdate?.data?.error === 0) {
                localStorage.setItem('selectedLocation', selected? selected.toString() : "")
                localStorage.setItem('selectedBay','') 
                localStorage.removeItem('selectedBay')  // Reset selected bay
                localStorage.removeItem('selectedBayType')
                if(location) {
                    setSelectedLocation({locationId:location?.locationId,locationName:location?.locationName,address:location?.address1});
                    setActiveLocale({
                        countryCode: location.countryCode,
                        currency: location.currency,
                        locale: location.locale,
                        timezone: location.timezone,
                    });
                }
                syncFreeSingleWashByLocation(Number(selected || 0));
                
                if(frompath === 'signup' || frompath === 'product-signup')
                {
                    if (frompath === 'product-signup' && navigateToSharePaymentIfPending(navigate, params.carwashname || '')) {
                        return;
                    }
                    if (frompath === 'product-signup') {
                        // share payment data missing — fall through to home
                        navigate("/"+params.carwashname+"/"+pageURLs.home);
                        return;
                    }
                    getBayTypes().then((bayTypes: IBayType[]) => {
                        if( bayTypes?.length > 1)
                        {
                        navigate("/" + params.carwashname + "/" + pageURLs.selectBayType + "?from=signup" );
                        }
                        
                        else if(bayTypes[0]?.bayTypeId == ServiceType.selfServe || bayTypes[0]?.bayTypeId == ServiceType.dogWash || bayTypes[0]?.bayTypeId == ServiceType.vacuumWash)
                        {
                            navigate("/"+params.carwashname+"/"+pageURLs.selfServeSubscriptionandwashprice+"?bayTypeId="+bayTypes[0]?.bayTypeId);
                        }
                            
                        else
                        {
                            navigate("/"+params.carwashname+"/"+pageURLs.subscriptionandwashprice);
                        }
                        
                
                    });

                    // navigate("/" + params.carwashname + "/" + pageURLs.selectBayType + "?from=signup");
                    // navigate("/"+params.carwashname+"/"+pageURLs.signupsubscriptionandwashpricelist);
                }
                else
                {
                    navigate("/"+params.carwashname+"/"+pageURLs.home);
                }
            } else {
                toaster.toastError(locationUpdate.data.error);
            }

        } else {
            setError(CommonMessages.location_required)
        }
    };
    //User selected location details
    const fetchSelectedUserLocation = async () => {
        const { data } = await axios.get(LocationApi.SELECTED_LOCATION_DETAILS);
        if (data?.error === 0) {
            localStorage.setItem('selectedLocation', data?.data?.locationId)
            const address = [];
            if(data?.data?.hasDefaultAddress === 1) {
                if(data?.data?.Address1) {
                  address.push(data?.data?.Address1)
                }
                if(data?.data?.Address2) {
                  address.push(data?.data?.Address2)
                }
                if(data?.data?.City) {
                  address.push(data?.data?.City)
                }
                if(data?.data?.State) {
                  address.push(data?.data?.State)
                }
                if(data?.data?.ZipCode) {
                  address.push(data?.data?.ZipCode)
                }
              } else {
                if(data?.data?.address1) {
                  address.push(data?.data?.address1)
                }
                if(data?.data?.address2) {
                  address.push(data?.data?.address2)
                }
                if(data?.data?.city) {
                  address.push(data?.data?.city)
                }
                if(data?.data?.state) {
                  address.push(data?.data?.state)
                }
                if(data?.data?.zipCode) {
                  address.push(data?.data?.zipCode)
                }
              }
            
            const selectedLocationDetails = {
                "locationName": data?.data?.locationName,
                "address": address.join(', '),
                "locationId" : data?.data?.locationId,
            }
            setSelectedLocation(selectedLocationDetails)
        }
      }
    return (
        <div className={origin === "Settings" ? "common-content-wrap home-content-wrap pb-170" : "common-content-wrap pb-115"}>
            <div className={origin === "Settings" ? "inner-content-wrap" : "inner-content-wrap signup-block"}>
                <div className={origin === "Settings" ? "common-header multi-group-header" : "common-header"}>
                    <div className="pe-4 ps-4 pt-0 pb-0 h-100">
                        <div className="common-header-inner">
                            {origin === "Settings" ? <> <Link to={"/"+params.carwashname+"/"+pageURLs.settings} className="back-link"><img src={Gobackicon} alt="Car Wash App" /></Link>
                                <img src={Mappinicon} alt="Car Wash App" />
                                <div className="info">
                                {selectedLocation?.locationName ? <h4>{selectedLocation?.locationName}</h4> : <h4>{carwashcompanydata?.companyName}</h4>}
                                <p>{selectedLocation?.address}</p>
                                </div></> : <div className="common-header-inner">Select Wash Location</div>}
                        </div>
                    </div>
                </div>
                <div className={origin === "Settings" ? "main-content-wrap pe-0 ps-0 switch-location-wrap" : "main-content-wrap pe-0 ps-0 pt-0"}>
                    {origin === "Settings" ? <div className="switch-title"><h3>Switch Location</h3></div> : null}

                    {
                        // To remove selected location from list
                        locations.length>0 && locations.filter((loc) => loc.locationId != selectedLocation?.locationId).map((location, index) => (
                            <div key={index} className="location-info-card">
                                <RadioButton name={"select_location"} label={location.locationName} value={location.locationId} description = {location.address1 ? location.address1 : ""} onChange={handleRadioChange} />
                            </div>
                        ))
                    }

                </div>
                <div className={origin === "Settings" ? "btm-secondarybtn-wrap btm-fixed-wrap" : "btm-wrap"}>
                    <div className="btm-inner-wrap">
                        {error ? <span className="error-msg">{error}</span> : null}
                        <Button variant="primary" className="primary-btn btn-main" onClick={handleOkButtonClick}>OK</Button>
                    </div>
                </div>
                {origin === "Settings" ? <CWFooter activeLink="Settings"/> : null}
            </div>
        </div>
    )

};

export default SwitchLocation;
